'use client';
import React, { useState, useRef, useEffect } from 'react';

const included = [
  { icon: '⚡', text: 'Instant access to all workflows' },
  { icon: '🔄', text: 'All future updates included — forever' },
  { icon: '🧬', text: 'Creator Realism Systems' },
  { icon: '🔥', text: 'Viral Hook Frameworks' },
  { icon: '🤖', text: 'AI Influencer Workflow Systems' },
  { icon: '💎', text: 'Luxury Brand Campaign Systems' },
  { icon: '✅', text: 'Full commercial usage rights' },
  { icon: '🎓', text: 'Step-by-step prompt guides' },
];

export default function PricingSection() {
  const [couponCopied, setCouponCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pricing-active');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (cardRef?.current) observer?.observe(cardRef?.current);
    return () => observer?.disconnect();
  }, []);

  const handleCopyCoupon = () => {
    navigator.clipboard?.writeText('EARLYACCESS35')?.then(() => {
      setCouponCopied(true);
      setTimeout(() => setCouponCopied(false), 2500);
    });
  };

  return (
    <section id="pricing" className="relative bg-[#080810] py-28 px-6 md:px-12 overflow-hidden">
      {/* Ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.1) 0%, transparent 70%)', filter: 'blur(60px)' }}
      />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/20 bg-violet-500/5 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
            <span className="font-sans text-xs text-violet-300 tracking-wide">Limited Founding Member Access</span>
          </div>
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-white leading-tight mb-4">
            One price.{' '}
            <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
              Everything included.
            </span>
          </h2>
          <p className="font-sans text-lg text-white/40">
            Get lifetime access before the price increases.
          </p>
        </div>

        {/* Pricing card */}
        <div
          ref={cardRef}
          className="pricing-card relative rounded-3xl overflow-hidden"
        >
          {/* Glow border */}
          <div className="absolute inset-0 rounded-3xl p-px bg-gradient-to-br from-violet-500/50 via-purple-500/30 to-pink-500/50">
            <div className="absolute inset-0 rounded-3xl bg-[#0d0d1a]" />
          </div>

          {/* Inner glow */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-32 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.2) 0%, transparent 70%)', filter: 'blur(20px)' }}
          />

          <div className="relative z-10 p-8 md:p-10">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/20 border border-violet-500/30 mb-8">
              <span className="text-sm">🏆</span>
              <span className="font-sans text-xs font-semibold text-violet-300 tracking-wide">Founding Member Access</span>
            </div>

            {/* Price */}
            <div className="flex items-end gap-3 mb-2">
              <div className="flex items-start gap-1">
                <span className="font-sans text-2xl text-white/40 mt-2">$</span>
                <span className="font-sans font-bold text-7xl text-white leading-none">39</span>
              </div>
              <div className="mb-3">
                <div className="font-sans text-sm text-white/30 line-through">$99</div>
                <div className="font-sans text-sm text-white/50">one-time</div>
              </div>
            </div>

            <p className="font-sans text-sm text-white/40 mb-8">
              Lifetime access · No subscription · No hidden fees
            </p>

            {/* Coupon code */}
            <div className="mb-8 p-4 rounded-2xl border border-amber-500/20 bg-amber-500/5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-sans text-xs text-amber-300/70 tracking-wide mb-1">🎁 Early Access Discount — 35% OFF</div>
                  <div className="font-sans font-bold text-lg text-amber-300 tracking-widest">EARLYACCESS35</div>
                </div>
                <button
                  onClick={handleCopyCoupon}
                  className="flex-shrink-0 px-4 py-2 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-colors duration-200 font-sans text-xs font-medium text-amber-300"
                >
                  {couponCopied ? '✓ Copied!' : 'Copy Code'}
                </button>
              </div>
              <div className="mt-2 font-sans text-xs text-white/30">
                Apply at checkout to get 35% off your founding member price
              </div>
            </div>

            {/* What's included */}
            <div className="space-y-3 mb-8">
              {included?.map((item) => (
                <div key={item?.text} className="flex items-center gap-3">
                  <span className="text-base flex-shrink-0">{item?.icon}</span>
                  <span className="font-sans text-sm text-white/65">{item?.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <a
              href="https://payhip.com/b/H2qt9"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-sans font-semibold text-base text-white overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7, #ec4899)' }}
            >
              {/* Glow effect */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'linear-gradient(135deg, #6d28d9, #9333ea, #db2777)', boxShadow: '0 0 40px rgba(139,92,246,0.5)' }}
              />
              <span className="relative z-10">Get Instant Access — $39</span>
              <svg className="relative z-10 group-hover:translate-x-1 transition-transform duration-200" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 9H14M10 5L14 9L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>

            {/* Trust note */}
            <div className="flex items-center justify-center gap-6 mt-6">
              {['🔒 Secure Checkout', '💳 One-Time Payment', '✅ Instant Access']?.map((item) => (
                <span key={item} className="font-sans text-xs text-white/25">{item}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Urgency note */}
        <p className="text-center font-sans text-sm text-white/30 mt-6">
          ⚡ Founding member pricing ends when spots fill — price increases to $99 after launch
        </p>
      </div>
      <style jsx>{`
        .pricing-card {
          opacity: 0;
          transform: translateY(32px) scale(0.98);
          transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .pricing-active {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      `}</style>
    </section>
  );
}
